import InnerContainer from "@/components/ContentContainer/InnerContainer";
import InputUnitConversion from "@/components/InputUnitConversion/InputUnitConversion";
import InputUnitConversionSkeleton from "@/components/InputUnitConversion/InputUnitConversionSkeleton";
import useAnalytics from "@/hooks/useAnalytics";
import useSmartQueryClient from "@/hooks/useSmartQueryClient";
import { message } from "antd";
import _ from "lodash";
import { useCallback, useMemo } from "react";
import {
  GetPackagingMaterialUnits,
  useGetPackagingMaterialUnits,
  useUpdatePackagingMaterialUnit,
} from "../usePackagingMaterials";
import styles from "./PackagingMaterialDetail.module.scss";

export default function PackagingMaterialUnitConversion({ materialId }: { materialId: number }) {
  const updateUnit = useUpdatePackagingMaterialUnit();
  const { data, isLoading } = useGetPackagingMaterialUnits({ materialId });
  const smartQueryClient = useSmartQueryClient();
  const analytics = useAnalytics();

  const saveUnit = useCallback((startUnit: string, targetUnit: string, factor: number | null) => {
    updateUnit
      .mutateAsync({
        materialId,
        materialUnit: { start_unit: startUnit, target_unit: targetUnit, factor },
      })
      .then(() => {
        analytics.track(`Packaging Material Unit Updated`);
      })
      .catch(() => {
        message.error("Something went wrong when updating material unit. Please try again.");
      });
  }, []);

  const debouncedSaveUnit = useMemo(() => _.debounce(saveUnit, 500), [saveUnit]);

  const onChangeUnit = (startUnit: string, targetUnit: string, factor: number | null) => {
    smartQueryClient.setQueryData<GetPackagingMaterialUnits>(
      ["material", materialId, "units"],
      (materialUnits) => {
        return materialUnits
          ? materialUnits.map((unit) => {
              if (unit.start_unit === startUnit && unit.target_unit === targetUnit) {
                return {
                  ...unit,
                  status: "validating",
                  factor: factor,
                };
              } else {
                return unit;
              }
            })
          : undefined;
      }
    );

    debouncedSaveUnit(startUnit, targetUnit, factor);
  };

  const isIncomplete = data?.some((x) => x.status === "incomplete");

  return (
    <InnerContainer
      title="Unit conversion"
      borderTop={true}
      contentClassName={styles.MaterialUnitConversion}
    >
      <div className={[styles.conversions, isIncomplete ? styles["incomplete"] : ""].join(" ")}>
        {isLoading ? (
          <div className={styles.conversion}>
            <InputUnitConversionSkeleton />
          </div>
        ) : data?.length === 0 ? (
          <div className={styles.message}>No unit conversion needed.</div>
        ) : (
          data?.map((unit) => (
            <div
              className={styles.conversion}
              key={unit.start_unit + unit.target_unit}
            >
              <InputUnitConversion
                sourceUnit={unit.start_unit}
                targetUnit={unit.target_unit}
                onChange={(value) => {
                  onChangeUnit(unit.start_unit, unit.target_unit, value);
                }}
                defaultValue={unit.conversion_factor || undefined}
                rootStatus={unit.status}
              />
            </div>
          ))
        )}
      </div>
    </InnerContainer>
  );
}
